import React from 'react';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import LabelChip, { PROPERTY_LABEL_CHIP_COLOR, labelColorToCss } from 'src/components/labels/LabelChip';
import type { IPropertyLabel } from 'src/services/api';

import styles from 'src/components/labels/LabelChips.module.css';

type LabelLike = Pick<IPropertyLabel, 'id' | 'name' | 'color'>;

type Props = {
    labels: LabelLike[];
    maxVisible?: number;
    size?: 'compact' | 'body';
    multiline?: boolean;
};

/**
 * Renders a row of property-label chips, capped at ``maxVisible`` with a
 * ``+N`` overflow chip. Chips are tinted with the label's stored color,
 * falling back to grey for labels without one.
 */
export default function PropertyLabels({
    labels,
    maxVisible = 2,
    size = 'body',
    multiline = false,
}: Props) {
    const visibleLabels = labels.slice(0, maxVisible);
    const hiddenLabels = labels.slice(maxVisible);
    const numRemainingLabels = labels.length - visibleLabels.length;
    const isBodySize = size === 'body';

    const colorOf = (label: LabelLike) => labelColorToCss(label.color) || PROPERTY_LABEL_CHIP_COLOR;

    if (labels.length === 0) {
        return null;
    }

    return (
        <div className={`${styles.root} ${multiline ? styles.multiline : ''}`}>
            {visibleLabels.map((label) => (
                <LabelChip key={label.id} name={label.name} color={colorOf(label)} size={size} />
            ))}

            {numRemainingLabels > 0 && (
                <Tooltip title={hiddenLabels.map((label) => label.name).join(', ')}>
                    <div
                        className={`${styles.remaining} ${isBodySize ? styles.remainingBody : ''}`}
                        style={{ '--label-color': colorOf(hiddenLabels[0]) } as React.CSSProperties}
                    >
                        <Typography variant="caption" className={styles.text}>
                            +{numRemainingLabels}
                        </Typography>
                    </div>
                </Tooltip>
            )}
        </div>
    );
}
